"use client";
import React from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/admin/ui/table";
import { Select } from "@/components/admin/ui/select";
import { Button } from "@/components/admin/ui/button";

export type Column<T> = {
  key: keyof T | string;
  header: string;
  width?: string;
  sortable?: boolean;
  render?: (row: T) => React.ReactNode;
  sortAccessor?: (row: T) => string | number | boolean | null | undefined;
};

type Props<T> = {
  rows: T[];
  columns: Column<T>[];
  idAccessor: (row: T) => React.Key;
  pageSizeDefault?: number; // default 25
  queryKeys?: { page?: string; size?: string; sort?: string; dir?: string };
  emptyLabel?: string;
  filterer?: (row: T, params: URLSearchParams) => boolean; // optional client-side filter predicate
  persistKey?: string; // defaults to pathname
  // Server-driven pagination: `rows` is already the current page and `totalCount`
  // is the grand total. The page control is driven by totalCount and rows are NOT
  // re-sliced client-side (the parent refetches on page/size changes).
  serverMode?: boolean;
  totalCount?: number;
};

export function DataTable<T>({ rows, columns, idAccessor, pageSizeDefault = 25, queryKeys, emptyLabel = 'No results', filterer, persistKey, serverMode, totalCount }: Props<T>) {
  const router = useRouter();
  const pathname = usePathname();
  const sp = useSearchParams();
  const pkey = persistKey || pathname || '/admin';
  const restoredRef = React.useRef(false);

  const qk = {
    page: queryKeys?.page || 'page',
    size: queryKeys?.size || 'size',
    sort: queryKeys?.sort || 'sort',
    dir: queryKeys?.dir || 'dir',
  };

  const page = Math.max(1, parseInt(sp?.get(qk.page) || '1', 10) || 1);
  const size = Math.max(1, parseInt(sp?.get(qk.size) || String(pageSizeDefault), 10) || pageSizeDefault);
  const sort = sp?.get(qk.sort) || '';
  const dir = (sp?.get(qk.dir) || 'asc') as 'asc' | 'desc';

  // On first mount, if URL lacks sort/size but we have saved, restore
  React.useEffect(() => {
    if (restoredRef.current) return;
    try {
      const hasAny = (sp?.get(qk.sort) || sp?.get(qk.size) || '') !== '';
      if (!hasAny && typeof window !== 'undefined') {
        const raw = localStorage.getItem(`table:${pkey}`);
        if (raw) {
          const saved = JSON.parse(raw) as { sort?: string; dir?: 'asc'|'desc'; size?: number };
          const next = new URLSearchParams(sp?.toString() || '');
          if (saved.sort) next.set(qk.sort, saved.sort);
          if (saved.dir) next.set(qk.dir, saved.dir);
          if (saved.size) next.set(qk.size, String(saved.size));
          router.replace(`${pathname}?${next.toString()}`);
        }
      }
    } catch {}
    finally { restoredRef.current = true; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setParams(patch: Record<string, string | null | undefined>) {
    const next = new URLSearchParams(sp?.toString() || '');
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === '') next.delete(k); else next.set(k, v);
    }
    router.replace(`${pathname}?${next.toString()}`);
  }

  function toggleSort(colKey: string) {
    if (sort === colKey) {
      setParams({ [qk.dir]: dir === 'asc' ? 'desc' : 'asc' });
    } else {
      setParams({ [qk.sort]: colKey, [qk.dir]: 'asc' });
    }
    try {
      const saved = JSON.parse(localStorage.getItem(`table:${pkey}`) || '{}');
      saved.sort = colKey; saved.dir = sort === colKey && dir === 'asc' ? 'desc' : 'asc';
      localStorage.setItem(`table:${pkey}`, JSON.stringify(saved));
    } catch {}
  }

  // Optional client-side filter based on current query params (skipped in server
  // mode, where the API already applied the filters).
  const filtered = (filterer && !serverMode) ? rows.filter(r => filterer!(r, new URLSearchParams(sp?.toString() || ''))) : rows;
  const sorted = [...filtered];
  if (sort) {
    const col = columns.find(c => (typeof c.key === 'string' ? c.key : String(c.key)) === sort);
    const acc = col?.sortAccessor || ((r: any) => r[sort as any]);
    sorted.sort((a: any, b: any) => {
      const av = acc(a); const bv = acc(b);
      // Numeric columns (sortAccessor returns a number: login counts, dates as
      // epoch ms, onboard stage) must compare numerically — the old code
      // stringified every value, so "9" sorted after "29". Text falls back to a
      // natural, numeric-aware compare (null -> '' still sorts first).
      if (typeof av === 'number' && typeof bv === 'number') {
        return dir === 'asc' ? av - bv : bv - av;
      }
      const an = av == null ? '' : String(av).toLowerCase();
      const bn = bv == null ? '' : String(bv).toLowerCase();
      const cmp = an.localeCompare(bn, undefined, { numeric: true });
      return dir === 'asc' ? cmp : -cmp;
    });
  }

  // Server mode drives the page control from the grand total the parent reports
  // and does NOT re-slice (client-slicing a single fetched page is exactly what
  // capped the People list at one page). Client mode slices as before.
  const total = serverMode ? (typeof totalCount === 'number' ? totalCount : sorted.length) : sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / size));
  const clampedPage = Math.min(page, totalPages);
  const start = (clampedPage - 1) * size;
  const pageRows = serverMode ? sorted : sorted.slice(start, start + size);

  function setPage(p: number) { setParams({ [qk.page]: String(Math.max(1, Math.min(p, totalPages))) }); }
  function setSize(s: number) { setParams({ [qk.size]: String(s), [qk.page]: '1' }); try { const saved = JSON.parse(localStorage.getItem(`table:${pkey}`) || '{}'); saved.size = s; localStorage.setItem(`table:${pkey}`, JSON.stringify(saved)); } catch {} }

  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto rounded-md border">
        <Table className="min-w-full text-sm">
          <THead className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <Tr className="text-left border-b">
              {columns.map((c) => {
                const key = typeof c.key === 'string' ? c.key : String(c.key);
                const isActive = sort === key;
                const canSort = c.sortable !== false;
                return (
                  <Th key={key} style={c.width ? { width: c.width } : undefined}>
                    {canSort ? (
                      <button className="inline-flex items-center gap-1 hover:underline" onClick={() => toggleSort(key)}>
                        {c.header}
                        {isActive && (<span aria-hidden>{dir === 'asc' ? '▲' : '▼'}</span>)}
                      </button>
                    ) : c.header}
                  </Th>
                );
              })}
            </Tr>
          </THead>
          <TBody>
            {pageRows.length === 0 ? (
              <Tr><Td colSpan={columns.length} className="py-10 text-center text-muted-foreground">{emptyLabel}</Td></Tr>
            ) : (
              pageRows.map((row) => (
                <Tr key={idAccessor(row)} className="border-b align-middle odd:bg-muted/30 hover:bg-accent/10">
                  {columns.map((c) => (
                    <Td key={String(c.key)} className="py-2 pr-3">
                      {c.render ? c.render(row) : String((row as any)[c.key as any] ?? '')}
                    </Td>
                  ))}
                </Tr>
              ))
            )}
          </TBody>
        </Table>
      </div>

      <div className="flex items-center gap-3 p-3">
        <div className="text-xs text-muted-foreground">{total === 0 ? '0' : `${start + 1}-${Math.min(start + pageRows.length, total)}`} of {total}</div>
        <div className="ml-auto flex items-center gap-2">
          <Button className="text-sm" onClick={() => setPage(1)} disabled={clampedPage <= 1} aria-label="First">«</Button>
          <Button className="text-sm" onClick={() => setPage(clampedPage - 1)} disabled={clampedPage <= 1} aria-label="Previous">‹</Button>
          <span className="text-sm">Page {clampedPage} / {totalPages}</span>
          <Button className="text-sm" onClick={() => setPage(clampedPage + 1)} disabled={clampedPage >= totalPages} aria-label="Next">›</Button>
          <Button className="text-sm" onClick={() => setPage(totalPages)} disabled={clampedPage >= totalPages} aria-label="Last">»</Button>
          <Select className="ml-2 h-8 text-sm" value={String(size)} onChange={(e) => setSize(parseInt((e.currentTarget as HTMLSelectElement).value, 10))} aria-label="Rows per page">
            {[10, 25, 50, 100, 200].map(n => (<option key={n} value={n}>{n} / page</option>))}
          </Select>
        </div>
      </div>
    </div>
  );
}

export default DataTable;
